export type OfflineDonationType = "donation" | "gaushala" | "healthcare";

export type OfflineDonationPaymentMode =
  | "cash"
  | "cheque"
  | "upi"
  | "neft"
  | "rtgs"
  | "imps"
  | "bank_transfer";

export interface OfflineDonationPaymentDetails {
  bank_name?: string;
  branch?: string;
  bank_transfer_id?: string;
  /** Online (Razorpay) payments carry gateway-specific keys such as wallet, vpa, bank, contact. */
  [key: string]: unknown;
}

export interface OfflineDonationParticular {
  name: string;
  amount: string;
}

export type OfflineDonationMode = "online" | "offline";

export interface OfflineDonationPayment {
  id: number;
  razorpay_order_id?: string | null;
  razorpay_payment_id?: string | null;
  receipt_no?: string;
  amount: string;
  currency?: string;
  status?: string;
  payment_method?: string;
  payment_method_details?: OfflineDonationPaymentDetails | null;
  payment_reference?: string;
  created_at?: string;
}

export interface OfflineDonation {
  id: number;
  type: OfflineDonationType;
  mode?: OfflineDonationMode | string;
  payments?: OfflineDonationPayment[];
  receipt_no?: string;
  category?: string;
  sub_category?: string;
  maintenance_schemes?: string;
  donor_name: string;
  display_name?: string;
  mobile_number: string;
  email?: string;
  city?: string;
  full_address?: string;
  state?: string;
  pincode?: string;
  country?: string;
  pan_number?: string;
  occasion?: string;
  special_instructions?: string;
  is_anonymous?: boolean;
  info_confirmed?: boolean;
  contact_consent?: boolean;
  amount: string;
  payment_mode?: OfflineDonationPaymentMode | string;
  payment_method_details?: OfflineDonationPaymentDetails | null;
  particulars?: OfflineDonationParticular[];
  status?: string;
  created_at?: string;
  updated_at?: string;
}

/** Payment details live on the latest payment record; fall back to the root field. */
export function getOfflineDonationPaymentDetails(
  donation: OfflineDonation
): OfflineDonationPaymentDetails {
  return (
    donation.payments?.[0]?.payment_method_details ??
    donation.payment_method_details ??
    {}
  );
}

export interface OfflineDonationPayload {
  type: OfflineDonationType;
  category: string;
  sub_category: string;
  donor_name: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  pan_number: string;
  amount: string;
  info_confirmed: boolean;
  payment_mode: OfflineDonationPaymentMode;
  payment_method_details?: OfflineDonationPaymentDetails;
  particulars: OfflineDonationParticular[];
}

/** The three trusts a manual receipt can be issued for, keyed by API `type`. */
export const OFFLINE_DONATION_TRUSTS: Record<
  OfflineDonationType,
  { title: string; subtitle: string; defaultCategory: string }
> = {
  donation: {
    title: "Manav Seva Kalyan Trust",
    subtitle: "General donations & elder care",
    defaultCategory: "General Donation",
  },
  gaushala: {
    title: "Vrundavan Gir Gaushala",
    subtitle: "Gau seva & gaushala donations",
    defaultCategory: "Gaushala",
  },
  healthcare: {
    title: "Bhagwan Mahavir Medical Centre",
    subtitle: "Medical & healthcare donations",
    defaultCategory: "Medical",
  },
};

export const OFFLINE_PAYMENT_MODE_OPTIONS: {
  value: OfflineDonationPaymentMode;
  label: string;
}[] = [
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "upi", label: "UPI" },
  { value: "neft", label: "NEFT" },
  { value: "rtgs", label: "RTGS" },
  { value: "imps", label: "IMPS" },
  { value: "bank_transfer", label: "Bank Transfer" },
];

/** Fixed particulars printed on each trust's paper receipt book, keyed by API `type`. */
export const OFFLINE_DONATION_PARTICULARS: Record<
  OfflineDonationType,
  string[]
> = {
  donation: [
    "For Ashram",
    "For Grains and Grocery",
    "For Medical",
    "For Permanent (Kayam) Date Bhojan Tithi",
    "For Bhojanshala",
    "For Jivdaya",
    "For General Fund",
    "For Devdravya",
    "For Other Particulars",
  ],
  gaushala: [
    "ગૌશાળા ખાતે",
    "સર્વ સાધારણ ખાતે",
    "ઘાસચારા ખાતે",
    "પક્ષીના ચણ ખાતે",
    "કાયમી તીથી ખાતે",
    "જીવ છોડાવવા ખાતે",
    "અન્ય ખાતે",
  ],
  healthcare: [
    "શ્રી મેડીકલ ખર્ચ ખાતે",
    "શ્રી વાત્સલ્યધામ ખાતે",
    "શ્રી ભૂમિદાન ખાતે",
    "શ્રી ગૌશાળા - જીવદયા ખાતે",
    "શ્રી ભોજનશાળા ખાતે",
    "શ્રી શૈક્ષણિક સહાય ખર્ચ ખાતે",
    "શ્રી અન્ય ખાતે",
  ],
};
